import { getAPILocale } from '@/utils';
import { fullContentFragment } from '@/utils/graphql/contentFragment';
import { gql } from '@apollo/client';

export const contentsVariables = (
  slug: any,
  languageCode: string = 'en',
  page?: number,
  draft?: boolean,
  limit?: number,
  type?: 'article' | 'video' | 'audio',
) => {
  const data: any = {
    where: {
      AND: [
        {
          slug: {
            equals: slug,
          },
        },
      ],
    },
    locale: getAPILocale(languageCode),
  };
  if (page) {
    data.page = page;
  }
  if (limit) {
    data.limit = limit;
  }
  if (draft) {
    data.draft = true;
  } else {
    data.where.AND.push({
      status: {
        in: ['published', 'changed'],
      },
    });
  }
  if (type) {
    data.where.AND.push({
      type: {
        equals: type,
      },
    });
  }
  return data;
};

export const contentsLoadMoreVariables = ({
  existingIds,
  languageCode,
  channelId,
  page = 0,
  primaryTopicId,
  type,
  draft,
  limit = 10,
}: {
  existingIds: string[];
  languageCode: string;
  channelId?: string;
  page: number;
  primaryTopicId?: string;
  type?: 'opinion' | 'feature';
  draft?: boolean;
  limit?: number;
}) => {
  const data: any = {
    where: {
      AND: [
        {
          type: {
            equals: 'article',
          },
        },
        // {
        //   package: {
        //     exists: true,
        //   },
        // },
        {
          status: {
            in: ['published', 'changed'],
          },
        },
        {
          OR: [
            {
              translatedToChannels: {
                equals: channelId,
              },
            },
            {
              channel: {
                equals: channelId,
              },
            },
          ],
        },
        {
          id: {
            not_in: existingIds,
          },
        },
      ],
    },
    locale: getAPILocale(languageCode),
    page,
    limit,
  };

  if (draft) {
    data.draft = true;
  }
  if (primaryTopicId) {
    data.where.AND.push({
      OR: [
        {
          primaryTopics: {
            in: [primaryTopicId],
          },
        },
        {
          secondaryTopics: {
            in: [primaryTopicId],
          },
        },
      ],
    });
  }
  if (type) {
    data.where.AND.push({
      subType: {
        equals: type,
      },
    });
  }

  return data;
};

export const tvShowContentsVariables = (
  slug: any,
  languageCode: string = 'en',
  page?: number,
  limit?: number,
  existingIds?: Array<string>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  topic?: Array<string>,
  isFeatured?: boolean,
) => {
  const data: any = {
    where: {
      AND: [
        {
          id: {
            not_in: existingIds,
          },
        },
        {
          status: {
            in: ['published', 'changed'],
          },
        },
        {
          subType: {
            in: ['tvContent'],
          },
        },
        ...(slug
          ? [
              {
                tvShow: {
                  equals: slug,
                },
              },
            ]
          : []),
      ],
    },
    locale: getAPILocale(languageCode),
  };

  if (page) {
    data.page = page;
  }
  if (limit) {
    data.limit = limit;
  }
  if (typeof isFeatured === 'boolean') {
    data.where.AND.push({
      isFeatured: {
        equals: isFeatured,
      },
    });
  }
  return data;
};

export const moreTvShowContentsVariables = (
  languageCode: string = 'en',
  page?: number,
  limit?: number,
  existingIds?: Array<string>,
  topic?: string,
) => {
  const data: any = {
    where: {
      AND: [
        {
          status: {
            in: ['published', 'changed'],
          },
        },
        {
          subType: {
            in: ['tvContent'],
          },
        },
        {
          id: {
            not_in: existingIds,
          },
        },
      ],
    },
    locale: getAPILocale(languageCode),
  };
  if (page) {
    data.page = page;
  }
  if (limit) {
    data.limit = limit;
  }
  if (topic) {
    data.where.AND.push({
      OR: [
        {
          primaryTopics: {
            in: [topic],
          },
        },
        {
          secondaryTopics: {
            in: [topic],
          },
        },
      ],
    });
  }

  return data;
};

export const discoverSearchContentsVariables = (
  ids: string[],
  channelId: string,
  limit?: number,
) => {
  const data: {
    limit?: number;
    locale: string | undefined;
    where: { [key: string]: object };
  } = {
    where: {
      AND: [
        {
          status: {
            in: ['published', 'changed'],
          },
        },
        {
          id: {
            in: ids,
          },
        },
      ],
    },
    locale: getAPILocale(channelId),
  };
  if (limit) {
    data.limit = limit;
  }
  return data;
};

export const contentsArticleSiteMapVariables = ({
  where,
  languageCode = 'en',
  page,
  draft,
  limit,
  type,
  sort,
}: {
  where: any;
  languageCode: string;
  page?: number;
  draft?: boolean;
  limit?: number;
  type?: 'article' | 'video' | 'audio';
  sort?: string;
}) => {
  const data: any = {
    where,
    locale: getAPILocale(languageCode),
  };
  if (page) {
    data.page = page;
  }
  if (limit) {
    data.limit = limit;
  }
  if (draft) {
    data.draft = true;
  }
  if (type) {
    if (!data.where.AND) {
      data.where.AND = [];
    }
    data.where.AND.push({
      type: {
        equals: type,
      },
    });
  }
  if (sort) {
    data.sort = sort;
  }
  return data;
};

export const contentsAuthorVariables = (
  author: any,
  languageCode: string = 'en',
  page?: number,
  draft?: boolean,
  limit?: number,
) => {
  const data: any = {
    where: {
      AND: [
        {
          OR: [
            {
              author__id_: {
                equals: author,
              },
            },
            {
              otherAuthors__id_: {
                in: [author],
              },
            },
          ],
        },
        {
          status: {
            in: ['published', 'changed'],
          },
        },
        {
          showByLine: {
            not_equals: false,
          },
        },
      ],
    },
    locale: getAPILocale(languageCode),
  };
  if (page) {
    data.page = page;
  }
  if (draft) {
    data.draft = true;
  }
  data.limit = limit || 10;

  return data;
};

export const contentVariables = (
  id?: string,
  languageCode: string = 'en',
  draft?: boolean,
) => {
  const data: any = {
    id,
    locale: getAPILocale(languageCode),
  };
  if (draft) {
    data.draft = true;
  }
  return data;
};

export const GET_CONTENTS_SOFT_POPULATE = gql`
  query GetContents(
    $where: Content_where
    $limit: Int
    $page: Int
    $draft: Boolean
    $locale: LocaleInputType
    $sort: String
  ) {
    Contents(
      where: $where
      locale: $locale
      limit: $limit
      page: $page
      draft: $draft
      sort: $sort
    ) {
      docs {
        id
        title
        description
        slug
        type
        subType
        newsSources
        publishedAt
        createdAt
        updatedAt
        audioNarration
        duration
        primaryTopics {
          page {
            slug
            name
          }
        }
        author {
          id_
          name
          profileUrl
          profilePicture {
            url
            type
            caption
            thumbnailUrl
          }
        }
        otherAuthors {
          id_
          name
          profileUrl
          profilePicture {
            url
            type
            caption
            thumbnailUrl
          }
        }
        coverImage
        media
        coverMedia
        readingTime {
          text
          minutes
        }
        websiteBlockDetails
      }
      hasNextPage
      hasPrevPage
      limit
      offset
      page
      pagingCounter
      totalDocs
    }
  }
`;

export const CHECK_CONTENT_EXISTS = gql`
  query GetContents(
    $where: Content_where
    $limit: Int
    $page: Int
    $draft: Boolean
    $locale: LocaleInputType
    $sort: String
  ) {
    Contents(
      where: $where
      locale: $locale
      limit: $limit
      page: $page
      draft: $draft
      sort: $sort
    ) {
      docs {
        id
      }
      hasNextPage
      hasPrevPage
      limit
      offset
      page
      pagingCounter
      totalDocs
      totalPages
    }
  }
`;

export const GET_FULL_CONTENTS = gql`
  query GetContents(
    $where: Content_where
    $limit: Int
    $page: Int
    $draft: Boolean
    $locale: LocaleInputType
    $sort: String
  ) {
    ${fullContentFragment}
  }
`;

export const GET_SITE_MAP_ARTICLECONTENTS = gql`
  query GetContents(
    $where: Content_where
    $limit: Int
    $page: Int
    $draft: Boolean
    $locale: LocaleInputType
  ) {
    Contents(
      where: $where
      locale: $locale
      limit: $limit
      page: $page
      draft: $draft
    ) {
      docs {
        id
        title
        description
        slug
        coverImage
        media
        coverMedia
        createdAt
        publishedAt
      }
      hasNextPage
      hasPrevPage
      limit
      offset
      page
      pagingCounter
      totalDocs
      totalPages
    }
  }
`;

export const GET_SITE_MAP_AUDIOCONTENTS = gql`
  query GetContents(
    $where: Content_where
    $limit: Int
    $page: Int
    $draft: Boolean
    $locale: LocaleInputType
  ) {
    Contents(
      where: $where
      locale: $locale
      limit: $limit
      page: $page
      draft: $draft
    ) {
      docs {
        id
        title
        slug
      }
      hasNextPage
      hasPrevPage
      limit
      offset
      page
      pagingCounter
      totalDocs
      totalPages
    }
  }
`;

export const GET_TV_SHOW_DATA = gql`
  query GetContents(
    $where: Content_where
    $limit: Int
    $page: Int
    $draft: Boolean
    $locale: LocaleInputType
  ) {
    Contents(
      where: $where
      locale: $locale
      limit: $limit
      page: $page
      draft: $draft
    ) {
      docs {
        id
        title
        slug
        tvShow {
          id
        }
      }
      hasNextPage
      hasPrevPage
      limit
      offset
      page
      pagingCounter
      totalDocs
      totalPages
    }
  }
`;

export const GET_CONTENT = gql`
  query GetContent($id: String!, $locale: LocaleInputType, $draft: Boolean) {
    Content(id: $id, locale: $locale, draft: $draft) {
      id
      body
      title
      description
      slug
      type
      subType
      publishedAt
      createdAt
      updatedAt
      audioNarration
      duration
      author {
        id_
        name
        profileUrl
        profilePicture {
          url
          type
          caption
          thumbnailUrl
        }
      }
      otherAuthors {
        id_
        name
        profileUrl
        profilePicture {
          url
          type
          caption
          thumbnailUrl
        }
      }
      coverImage
      media
      coverMedia

      primaryTopics {
        title
        id
      }
      secondaryTopics {
        title
        id
      }
      readingTime {
        text
        minutes
      }
      websiteBlockDetails
    }
  }
`;

export const GET_FIRST_AND_LAST_CONTENT = gql`
  query GetFirstAndLastContent(
    $where: Content_where
    $locale: LocaleInputType
  ) {
    firstContent: Contents(
      where: $where
      locale: $locale
      limit: 1
      sort: "-createdAt"
    ) {
      docs {
        id
        createdAt
        publishedAt
      }
    }
    lastContent: Contents(
      where: $where
      locale: $locale
      limit: 1
      sort: "createdAt"
    ) {
      docs {
        id
        createdAt
        publishedAt
      }
    }
  }
`;
